import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import APIserver from '../../../../api/axiosConfig';
import Swal from 'sweetalert2';
import imageCompression from 'browser-image-compression';

const CrearProductoTienda = ({ showPT, handleClosePT, setSelectedData }) => {
  const [categorias, setCategorias] = useState([]);
  // Se recomienda separar el estado de las opciones de referencia y la referencia seleccionada:
  const [opcionesReferencia, setOpcionesReferencia] = useState([]);
  const [referenciaSeleccionada, setReferenciaSeleccionada] = useState(null);
  const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState([]);
  const [categoria, setCategoria] = useState(null);
  // Ahora solo se usa para almacenar las imágenes ya comprimidas
  const [imagenesComprimidas, setImagenesComprimidas] = useState([]);
  const [contadorImagenes, setContadorImagenes] = useState(0);

  useEffect(() => {
    fetchCategorias();
    fetchReferencias();
  }, []);

  const fetchCategorias = async () => {
    try {
      const response = await APIserver.get('/api/categoria');
      if (Array.isArray(response.data)) {
        setCategorias(
          response.data.map((cat) => ({
            value: cat.idCategoria,
            label: cat.nomCategoria,
          }))
        );
      } else {
        console.error('La respuesta no es un array:', response.data);
      }
    } catch (error) {
      console.error('Error al obtener categorías:', error);
    }
  };

  const fetchReferencias = async () => {
    try {
      const response = await APIserver.get('/api/referencias');
      if (Array.isArray(response.data)) {
        setOpcionesReferencia(
          response.data.map((ref) => ({
            value: ref.idProducto,
            label: `${ref.referencia} - ${ref.nomProducto}`,
          }))
        );
      } else {
        console.error('La respuesta de referencias no es un array:', response.data);
      }
    } catch (error) {
      console.error('Error al obtener referencias:', error);
    }
  };

  const handleCategoriaChange = (selectedOption) => {
    setCategoria(selectedOption);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 50) {
      alert('No puedes seleccionar más de 50 imágenes.');
      return;
    }

    const validImages = files.filter((file) => file.type.startsWith('image/'));
    if (validImages.length !== files.length) {
      alert('Algunas de las imágenes seleccionadas no son válidas.');
    }

    setContadorImagenes(validImages.length);

    try {
      Swal.fire({
        title: 'Comprimiendo imágenes...',
        text: 'Por favor, espera mientras se comprimen las imágenes.',
        icon: 'info',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const compressedImages = await Promise.all(
        validImages.map(async (file) => {
          const options = {
            maxSizeMB: 1,
            useWebWorker: true,
          };
          const compressedBlob = await imageCompression(file, options);
          const fileName = `reymidas_${Date.now()}.webp`;
          return new File([compressedBlob], fileName, { type: 'image/webp' });
        })
      );
      setImagenesComprimidas(compressedImages);
      Swal.close();
    } catch (error) {
      console.error('Error al comprimir las imágenes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un problema al comprimir las imágenes.',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (imagenesComprimidas.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin imágenes',
        text: 'Debes seleccionar al menos una imagen.',
      });
      return;
    }

    if (!categoriasSeleccionadas || !referenciaSeleccionada) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos faltantes',
        text: 'Selecciona una categoría y una referencia.',
      });
      return;
    }

    const formData = new FormData();
    categoriasSeleccionadas.forEach((cat) => {
      formData.append('idCategorias[]', cat.value);
    });
    formData.append('idProducto', referenciaSeleccionada.value);

    imagenesComprimidas.forEach((file) => {
      formData.append('imagenes', file); // El nombre "imagenes" debe coincidir con lo que espera el backend
    });

    try {
      Swal.fire({
        title: 'Guardando...',
        text: 'Por favor, espera mientras se guarda el producto.',
        icon: 'info',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await APIserver.post('/api/crear-productos-tienda', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Producto guardado',
          text: 'Producto guardado exitosamente.',
        });
        handleClosePT();
      }
    } catch (error) {
      console.error('Error al guardar el producto:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al guardar el producto.',
      });
    }
  };

  return (
    <Modal show={showPT} onHide={handleClosePT} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Asignar Categoría e Imágenes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col>
              <Form.Group controlId="formReferencia" className="m-3 mt-0">
                <Form.Label>Referencia</Form.Label>
                <Select
                  options={opcionesReferencia}
                  value={referenciaSeleccionada}
                  onChange={(selectedOption) => setReferenciaSeleccionada(selectedOption)}
                  placeholder="Selecciona una referencia"
                  required
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formCategoria" className="m-3 mt-0">
                <Form.Label>Categoría</Form.Label>
                <Select
                  options={categorias}
                  value={categoriasSeleccionadas}
                  onChange={setCategoriasSeleccionadas}
                  placeholder="Selecciona una o varias categorías"
                  isMulti
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group controlId="formImagen" className="m-3">
            <Form.Label>Imágenes</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
            />
            <Form.Text className="text-muted">
              Puedes agregar múltiples imágenes. Actualmente seleccionadas: {contadorImagenes}
            </Form.Text>
          </Form.Group>

          <Modal.Footer>
            <Button variant="dark" onClick={handleClosePT}>
              Cancelar
            </Button>
            <Button variant="danger" type="submit">
              Guardar
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CrearProductoTienda;
